/*
 * File: /src/services/templateService.js
 * Created Date: Friday June 14th 2019
 * Author: Nicolas Wegener, Roy Collins
 * -----
 * Last Modified: Friday, 14th June 2019 8:58:27 pm
 * Modified By: Nicolas Wegener (wegenern@gmail.com)
 * -----
 * Copyright (c) 2019 AdBase, Inc.
 */
import {
  AD_BASE_TEMPLATE_MERGE_PATH,
  AD_BASE_TEMPLATES_PATH,
  S2_EMAIL_TEMPLATES_PATH,
  S2_WHATSAPP_TEMPLATE_PATH,
} from '../constants';
import { authedAxios } from '../shared/axiosWrapper';

export default {
  saveSystemSmsTemplate(sourceId, text, image) {
    return authedAxios.post(AD_BASE_TEMPLATES_PATH + 'system-sms/' + sourceId, {
      text,
      image,
    });
  },

  saveSystemPostcardTemplate(
    sourceId,
    name,
    uuidFront,
    uuidBack,
    placement,
    size
  ) {
    return authedAxios.post(
      AD_BASE_TEMPLATES_PATH + 'system-postcard/' + sourceId,
      {
        name,
        templateType: 'postcard',
        uuidFront,
        uuidBack,
        placement,
        size,
      }
    );
  },

  /**
   * List all Templates
   */
  fetchTemplates(archive, templateType) {
    const params = {
      template_type: 'email',
      archived: false,
      is_system: false,
      limit: 1000,
      sort: 'name:asc',
    };
    if (archive != undefined) {
      params['archived'] = archive;
    }
    if (templateType != undefined) {
      params['template_type'] = templateType;
    }

    return authedAxios.get(AD_BASE_TEMPLATES_PATH, { params: params });
  },

  fetchWhatappTemplates(archive, templateType) {
    const params = {
      limit: 1000,
      sort: 'name:asc',
    };

    return authedAxios.get(S2_WHATSAPP_TEMPLATE_PATH, { params: params });
  },

  /**
   * Fetch Template By ID
   *
   * @param {number} id
   * @returns <Promise>
   */
  fetchTemplate(id) {
    if (id === undefined) return;
    return authedAxios.get(AD_BASE_TEMPLATES_PATH + id + '/');
  },

  /**
   * Get the Preview HTML
   *
   * @param {*} id
   * @returns
   */
  fetchTemplatePreview(id) {
    return authedAxios.get(AD_BASE_TEMPLATES_PATH + 'preview/' + id + '/');
  },

  /**
   * Rename Template
   * @param {*} id
   * @param {*} name
   */
  renameTemplate(id, name) {
    return authedAxios.put(AD_BASE_TEMPLATES_PATH + id + '/rename', {
      name: name,
    });
  },

  /**
   * Save or Update Template
   *
   * @param {number} id  when [null | undefined], it will create a new template
   * @param {string} type template type
   * @param {string} name template name
   * @param {string} subject template subject
   * @param {string} preview template preview
   * @param {object} background background object
   * @param {object} header header object
   * @param {object} body body object
   * @param {object} footer footer object
   */
  saveTemplate(
    id,
    type,
    name,
    subject,
    preview,
    background,
    header,
    body,
    footer,
    system = false
  ) {
    const deliverable = {
      type: type,
      name: name,
      subject: subject,
      json_content: JSON.stringify({
        preview: preview,
        background: background,
        header: header,
        body: body,
        footer: footer,
      }),
      is_active: true,
      is_system: system,
    };
    // force new on is_system
    if (id === null || id === undefined || system) {
      return authedAxios.post(AD_BASE_TEMPLATES_PATH, deliverable);
    } else {
      return authedAxios.put(AD_BASE_TEMPLATES_PATH + id + '/', deliverable);
    }
  },

  /**
   * save SMS templates
   *
   * - new routes
   *
   * @param {*} id
   * @param {*} name
   * @param {*} text
   * @param {*} image
   * @param {*} imagePreview
   */
  saveSmsTemplate(id, name, text, image, imagePreview) {
    const deliverable = {
      name: name,
      type: 'sms',
      subject: '',
      text: text,
      image: image,
      imagePreview: imagePreview,
    };
    if (id === null || id === undefined || id === 0) {
      return authedAxios.post(AD_BASE_TEMPLATES_PATH + 'sms/', deliverable);
    } else {
      return authedAxios.put(
        AD_BASE_TEMPLATES_PATH + 'sms/' + id + '/',
        deliverable
      );
    }
  },

  savePostCardTemplate(id, name, uuidFront, uuidBack, placement, size) {
    const deliverable = {
      name: name,
      templateType: 'postcard',
      uuidFront: uuidFront,
      uuidBack: uuidBack,
      placement: placement,
      size: size,
    };
    if (id === null || id === undefined || id === 0) {
      return authedAxios.post(
        AD_BASE_TEMPLATES_PATH + 'postcard/',
        deliverable
      );
    } else {
      return authedAxios.put(
        AD_BASE_TEMPLATES_PATH + 'postcard/' + id + '/',
        deliverable
      );
    }
  },

  sendTestMessage(
    id,
    email,
    phone,
    fromAddressId = 0,
    subject = '',
    previewLine = ''
  ) {
    if (typeof email === 'object') {
      return authedAxios.post(S2_EMAIL_TEMPLATES_PATH + 'demo/' + id + '/', {
        email: email,
        subject: subject,
        previewLine: previewLine,
        fromAddressId: fromAddressId,
      });
    }
    if (typeof phone === 'object') {
      return authedAxios.post(AD_BASE_TEMPLATES_PATH + 'demo/' + id + '/', {
        phone: phone,
      });
    }
  },

  /**
   * Duplicate Template
   *
   * @param {*} id
   * @param {*} name
   * @returns
   */
  duplicateTemplate(id, name) {
    return authedAxios.post(AD_BASE_TEMPLATES_PATH + 'duplicate/' + id, {
      name: name,
    });
  },

  /**
   * Archive Template
   *
   * @param {number} id
   */
  archiveTemplate(id) {
    return authedAxios.put(AD_BASE_TEMPLATES_PATH + id + '/archive', {
      archive: true,
    });
  },

  /**
   * Un Archive Template
   *
   * @param {number} id
   */
  unArchiveTemplate(id) {
    return authedAxios.put(AD_BASE_TEMPLATES_PATH + id + '/archive', {
      archive: false,
    });
  },

  /**
   * Delete Template
   *
   * @param {number} id
   */
  deleteTemplate(id) {
    return authedAxios.delete(AD_BASE_TEMPLATES_PATH + id);
  },

  /**
   * Upload Thumbnail Data
   *
   * @param {*} id
   * @param {*} data
   * @returns
   */
  uploadThumbnail(id, data) {
    return authedAxios.put(AD_BASE_TEMPLATES_PATH + id + '/thumbnail', {
      image: data,
    });
  },

  /**
   * Change Template Folder
   *
   * @param {*} id
   * @param {*} folderId
   * @returns
   */
  changeFolder(id, folderId) {
    return authedAxios.put(AD_BASE_TEMPLATES_PATH + id + '/folder', {
      folderId: folderId,
    });
  },

  /**
   * Get Template Merge
   *
   * @param {*} template
   * @param {*} contactId
   * @param {*} campaignId
   * @param {*} reservationId
   * @param {*} leadId
   * @returns
   */
  getTemplateMerge(
    templateId,
    contactId,
    campaignId,
    reservationId,
    leadId,
    content,
    airbnbThreadId, // Our id not airbnbs id
  ) {
    const payload = {
      template_id: templateId,
      // Content will be provided with template id of 0
      content: content,
    };
    if (contactId) {
      payload.contact_id = contactId;
    }
    if (campaignId) {
      payload.campaign_id = campaignId;
    }
    if (reservationId) {
      payload.reservation_id = reservationId;
    }
    if (leadId) {
      payload.lead_id = leadId;
    }
    if (airbnbThreadId) {
      payload.airbnb_message_thread = airbnbThreadId;
    }

    return authedAxios.post(AD_BASE_TEMPLATES_PATH + 'merge/', payload);
  },

  /**
   * Patch template
   * @param {number} templateId
   * @param {templateInputModel} payload
   * @returns
   */
  patchTemplate(templateId, payload) {
    return authedAxios.patch(AD_BASE_TEMPLATES_PATH + templateId, payload);
  },
};
